import React, { useEffect, useState, useCallback, useRef } from 'react';
import mqtt from 'mqtt';
import {  Col, Flex, Row, Tree, Typography } from 'antd';
import { ReactFlowProvider } from 'reactflow';
import './Uns.css'
import UnsFlow from './UnsFlow';
import { Content } from 'antd/es/layout/layout';
const { DirectoryTree } = Tree;
const {Title} = Typography;

/**
 * Build subscription topic(s) from Firestore unsConfig.
 * Replicates the old buildTopicPath logic using prefix + branches.
 */
const buildTopicsFromConfig = (unsConfig) => {
  if (!unsConfig?.mqttConfig) return [];
  const prefix = (unsConfig.mqttConfig.topicPrefix || '').replace(/\/+$/, '');
  const branches = unsConfig.topicBranches || ['#'];

  return branches.map((branch) => {
    const base = prefix ? `${prefix}/${branch}` : branch;
    // If branch already has wildcards, use as-is; otherwise append /#
    if (branch.includes('+') || branch.includes('#')) return base;
    return `${base}/#`;
  });
};

const Uns = ({unsId, factoryData, hideTree = false}) => {
  // Read MQTT config from Firestore unsConfig on the factory document
  const unsConfig = factoryData?.unsConfig;
  const currentConfig = unsConfig?.mqttConfig || {};
  const topic = buildTopicsFromConfig(unsConfig);
  
  const [, setMqttMessage] = useState({});
  const [allMessages, setAllMessages] = useState({}); // eslint-disable-line no-unused-vars
  const [isConnected, setIsConnected] = useState(false); // eslint-disable-line no-unused-vars
  const [antTree, setAntTree] = useState([]);
  const [hotTopic, setHotTopic] = useState(); // For tracking blinking keys
  const [expandedKeys, setExpandedKeys] = useState([]);
  const expandedKeysRef = useRef([]);
  const isInitializedRef = useRef(false);
  const userCollapsedKeysRef = useRef(new Set());


  function isEmptyObject(obj) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  // Recursive function to merge two objects deeply
  const mergeDeep = useCallback((target = {}, source = {}) => {
    const result = { ...target };
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] instanceof Object && key in result && result[key] instanceof Object) {
          result[key] = mergeDeep(result[key], source[key]);
        } else {
          result[key] = source[key];
        }
      }
    }
    return result;
  }, []);

  // Function to update the existing message tree with new data
  const updateMessageTree = useCallback((prevMessages, newMessage) => {
    return mergeDeep(prevMessages, newMessage);
  }, [mergeDeep]);

  // Throttled hot topic setter to reduce re-renders
  const hotTopicTimeoutRef = useRef(null);
  const applyBlinkingEffect = useCallback((topicKey) => {
    // Clear any pending timeout
    if (hotTopicTimeoutRef.current) {
      clearTimeout(hotTopicTimeoutRef.current);
    }
    
    // Set the hot topic (this triggers UnsFlow highlight)
    setHotTopic(topicKey.toLowerCase());
    
    // Clear after delay
    hotTopicTimeoutRef.current = setTimeout(() => {
      setHotTopic(null);
    }, 500);
  }, []);
  
  const convertToTreeData = useCallback((obj, parentKey = '0', keyCounter = { current: 0 }) => {
    // Initialize an array to hold the tree nodes
    const treeNodes = [];
  
    // Iterate over each key in the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        // Skip the _topic property as it's metadata
        if (key === '_topic') continue;
        
        const nodeKey = `${parentKey}-${keyCounter.current}`; // Generate unique key
        const nodeTitle = key;
        const children = obj[key];
        
        const treeNode = {
            title: nodeTitle,
            key: nodeKey,
            dataTopic: children && children._topic ? children._topic : undefined,
          };
  
        // Check if the node has the _topic attribute
        if (children && typeof children === 'object' && children._topic) {
          treeNode['data-topic'] = children._topic;
        }
       
        // Check if the value is a primitive (string, number, boolean, null)
        if (children === null || typeof children === 'string' || typeof children === 'number' || typeof children === 'boolean') {
          // Display primitive values directly
          treeNode.title = (
            <span>
              <span>{nodeTitle}: </span>
              <span className="node-value">{children !== null ? children.toString() : 'null'}</span>
            </span>
          );
          treeNode.isLeaf = true;
          treeNodes.push(treeNode);
        } else if (typeof children === 'object' && children !== null && !isEmptyObject(children)) {
          // Recurse into objects
          treeNodes.push(treeNode);
          treeNode.children = convertToTreeData(children, nodeKey, keyCounter);
        } else {
          // Empty objects or other types
          treeNode.isLeaf = true;
          treeNodes.push(treeNode);
        }
        
        keyCounter.current++; // Increment the key counter for the next node
      }
    }
  
    return treeNodes; // Return the constructed tree nodes
  }, []);
  
  const handleExpand = useCallback((keys) => {
    const newKeys = new Set(keys);
    const prevKeys = new Set(expandedKeysRef.current);

    for (const k of prevKeys) {
      if (!newKeys.has(k)) userCollapsedKeysRef.current.add(k);
    }
    for (const k of newKeys) {
      if (!prevKeys.has(k)) userCollapsedKeysRef.current.delete(k);
    }

    setExpandedKeys(keys);
    expandedKeysRef.current = keys;
  }, []);

  const structureMessageByTopic = useCallback((topic, msg) => {
    const levels = topic.split('/'); // Split topic into levels
    
    // Skip the initial levels based on topicPrefix
    const prefixLevels = (currentConfig.topicPrefix || '').split('/');
    
    // Find where to start - look for the last part of the prefix in the topic
    const startIndex = levels.findIndex(level => level === prefixLevels[prefixLevels.length - 1]);
    
    // If found, use levels from that point onwards, otherwise use all levels
    const displayLevels = startIndex >= 0 ? levels.slice(startIndex) : levels;
    
    const nestedMessage = displayLevels.reduceRight((acc, level) => {
      if (acc && typeof acc === 'object' && Object.keys(acc).length !== 0) {
        // If it's the last level (the one containing actual data), add the topic
        if (displayLevels[displayLevels.length - 1] === level) {
          acc['_topic'] = topic; // Set the topic only at the final level
        }
      }

      return { [level]: acc }; // Build the hierarchy
    }, msg);
  
    return nestedMessage; // Return the nested message
  }, [currentConfig.topicPrefix]);


  useEffect(() => {
    // Don't connect if no MQTT config available yet
    if (!currentConfig.broker) return;

    console.log('🚀 MQTT Connection Setup for factory:', unsId);

    const clientId = `vtec_uns_${unsId}_${Date.now()}`;
    
    const client = mqtt.connect(currentConfig.broker, {
      username: currentConfig.username,
      password: currentConfig.password, 
      rejectUnauthorized: false,
      connectTimeout: 30 * 1000,
      reconnectPeriod: 5 * 1000,
      keepalive: 60,
      clean: true,
      clientId: clientId,
      protocolVersion: 4,
    });
    
    setMqttMessage({});
    setAntTree([]);
    setAllMessages({});
    isInitializedRef.current = false;
    expandedKeysRef.current = [];
    userCollapsedKeysRef.current = new Set();
    
    // Set up connection timeout
    const connectionTimeout = setTimeout(() => {
      console.error('⏰ MQTT connection timeout');
    }, 30000);
    
    // On connection
    client.on('connect', () => {
      clearTimeout(connectionTimeout);
      console.log('✅ Connected to MQTT broker');
      setIsConnected(true);
      
      // Subscribe to topic(s)
      const topics = Array.isArray(topic) ? topic : [topic];
      topics.forEach((t) => {
        client.subscribe(t, (err) => {
          if (err) console.error('Subscription error:', err);
        });
      });
    });
    
    // On reconnect
    client.on('reconnect', () => {
      console.log('🔄 Reconnecting...');
    });
    
    // On disconnect
    client.on('disconnect', () => {
      setIsConnected(false);
    });
    
    // On offline
    client.on('offline', () => {
      setIsConnected(false);
    });
    
    // Message handler
    client.on('message', (topic, message) => {
      let msg = {};
      try {
        msg = JSON.parse(message.toString());
      } catch (error) {
        return; // Skip invalid JSON silently
      }
      
      // Apply blinking effect
      applyBlinkingEffect(topic);
      
      // Structure and update the tree
      const tree = structureMessageByTopic(topic, msg);
      
      setMqttMessage(prevMessages => {
        const updated = updateMessageTree(prevMessages, tree);
        
        const newAntTree = convertToTreeData(updated);
        setAntTree(newAntTree);
        
        const allKeys = [];
        const flattenKeys = (nodes) => {
          nodes.forEach(node => {
            allKeys.push(node.key);
            if (node.children) {
              flattenKeys(node.children);
            }
          });
        };
        flattenKeys(newAntTree);

        if (!isInitializedRef.current) {
          setExpandedKeys(allKeys);
          expandedKeysRef.current = allKeys;
          isInitializedRef.current = true;
        } else {
          const currentKeys = new Set(expandedKeysRef.current);
          const collapsed = userCollapsedKeysRef.current;
          let hasNew = false;
          for (const k of allKeys) {
            if (!currentKeys.has(k)) {
              const underCollapsed = [...collapsed].some(
                c => k === c || k.startsWith(c + '/')
              );
              if (!underCollapsed) {
                currentKeys.add(k);
                hasNew = true;
              }
            }
          }
          if (hasNew) {
            const merged = Array.from(currentKeys);
            setExpandedKeys(merged);
            expandedKeysRef.current = merged;
          }
        }
        
        return updated;
      });
      
      // Update allMessages for reference
      setAllMessages(prev => ({ ...prev, [topic]: msg }));
    });

    // Handle connection errors
    client.on('error', (err) => {
      console.error('Connection error:', err);
    });

    // Cleanup connection on unmount
    return () => {
      const topics = Array.isArray(topic) ? topic : [topic];
      topics.forEach(t => {
        client.unsubscribe(t);
      });
      client.end();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsId]); // Only reconnect when factory ID changes


  if (hideTree) {
    return (
      <div className='antContainer' style={{ width: '100%', height: '100%' }}>
        <ReactFlowProvider>
          <UnsFlow hotTopic={hotTopic} flowId={unsId}/>
        </ReactFlowProvider>
      </div>
    );
  }

  return (
        <Row justify="space-between" className='antContainer' >
        <Col
        span={6} // Fixed size for tree column
        className={'unsFlex'}
        >
            <Flex 
            vertical>
            <Title level={5} style={{color:'#F2EEA1', marginTop:'5px'}}>Unified Name Space</Title>
                
                <Content className='customScroll' >
                    <div style={{flex:"1 1 auto", maxHeight:"100%"}}>
                    <DirectoryTree 
                        treeData={antTree} 
                        expandedKeys={expandedKeys}
                        onExpand={handleExpand}
                        showLine={true}
                        showIcon={false}
                        className={'tree'}
                        selectable={false}
                    />
                    </div>
                 </Content>
        </Flex>
        </Col>
      <Col
      flex="1"
      >

    <ReactFlowProvider>
        <UnsFlow hotTopic={hotTopic} flowId={unsId}/>

    </ReactFlowProvider>
      </Col>
        </Row>
  );
};

export default Uns;
